import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";

export default function CampaignDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/campaigns/${slug}`)
      .then((r) => setC(r.data))
      .catch(() => nav("/campaigns", { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-32 md:px-10">
        <div className="h-12 w-1/2 animate-pulse bg-[#EBE7DD]" />
        <div className="mt-6 aspect-[16/10] w-full animate-pulse bg-[#EBE7DD]" />
      </div>
    );
  }
  if (!c) return null;

  return (
    <article data-testid="campaign-detail" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title={c.title}
        path={`/campaigns/${c.slug}`}
        description={c.subtitle || c.description?.slice(0, 200) || c.title}
        image={c.hero_image_url ? fileUrl(c.hero_image_url) : undefined}
      />

      <div className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[1400px] px-6 pt-12 md:px-10 md:pt-20">
          <Link
            to="/campaigns"
            className="font-display inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.36em] text-[#1A3626]/70 transition-colors hover:text-[#1A3626] md:text-xs"
            data-testid="campaign-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.6} />
            The Lookbook
          </Link>
        </div>
      </div>

      <header className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[1100px] px-6 pb-10 pt-10 md:px-10 md:pb-14 md:pt-16">
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-[#1A3626]/70 md:text-xs">
            {c.season && (
              <span className="font-display font-black uppercase tracking-[0.42em] text-[#93C065]">
                {c.season}
              </span>
            )}
            {c.location && (
              <>
                <span aria-hidden>&middot;</span>
                <span className="font-display font-bold uppercase tracking-[0.32em]">
                  {c.location}
                </span>
              </>
            )}
          </div>
          <h1
            className="font-display mt-5 text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            data-testid="campaign-title"
          >
            {c.title}
          </h1>
          {c.subtitle && (
            <p className="mt-7 max-w-[640px] text-lg leading-relaxed text-[#1A3626]/90 md:text-2xl md:leading-[1.55]">
              {c.subtitle}
            </p>
          )}
        </div>
      </header>

      {c.hero_image_url && (
        <div className="bg-[#F9F9F4]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="aspect-[16/10] overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]">
              <img
                src={fileUrl(c.hero_image_url)}
                alt={c.hero_image_alt || c.title}
                className="h-full w-full object-cover"
                data-testid="campaign-hero"
              />
            </div>
          </div>
        </div>
      )}

      {c.description && (
        <section className="bg-[#F9F9F4]">
          <div className="mx-auto max-w-[760px] px-6 pb-10 pt-12 md:px-10 md:pb-14 md:pt-20">
            <div
              className="whitespace-pre-line text-base leading-relaxed text-[#1A3626] md:text-lg md:leading-[1.75]"
              data-testid="campaign-description"
            >
              {c.description}
            </div>
          </div>
        </section>
      )}

      {c.gallery?.length > 0 && (
        <section className="bg-[#F9F9F4] pb-24 md:pb-32" data-testid="campaign-gallery">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-10">
              {c.gallery.map((g, i) => (
                <div
                  key={i}
                  className="aspect-[4/5] overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]"
                >
                  <img
                    src={fileUrl(g.url)}
                    alt={g.alt || `${c.title} \u2014 ${i + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
